import { BLUE, DANGER, GREEN, PRIMARY, YELLOW } from '../Theme/palette';
import { AppUserTag } from '../Tag/AppUserTag';
import {
  BellOutlined,
  CalendarOutlined,
  CarOutlined,
  CommentOutlined,
  DownOutlined,
  EditOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  HistoryOutlined,
  ExportOutlined,
  HomeOutlined,
  KeyOutlined,
  MailOutlined,
  MessageOutlined,
  MobileOutlined,
  NumberOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  ChatDrawer,
  CustomerDetailsDrawer,
  CustomerEditDrawer,
  InvoiceAssignCustomerDrawer,
  RegPlate,
  RemindersDrawer,
  ServiceHistoryDrawer,
  SuccessButton,
  VehicleDetailsDrawer,
} from '@components';
import { useCustomerSmsInvite, useOverdueReminderCount, useAccess, useView } from '@hooks';
import { Customer, Invoice, NotificationService, Vehicle } from '@services';
import { useStore } from '@stores';
import { Alert, Badge, Button, Descriptions, Divider, Popconfirm, Popover, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { DetailsPanel } from './DetailsPanel';
import { SharedCard } from './SharedCard';

// SMS invites are paused while personalised per-garage invites are built. Flip back to true to
// restore the send-invite confirmation.
const SMS_INVITE_ENABLED = false;

// Field icons are rendered in a neutral grey rather than per-field colours.
const ICON_GREY = '#8c8c8c';

const textStyle: React.CSSProperties = {
  wordBreak: 'break-word',
  fontSize: 14,
  lineHeight: '1.4',
  flex: 1,
};

const emptyStyle: React.CSSProperties = { ...textStyle, color: 'rgba(0, 0, 0, 0.45)' };

const labelStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };

// --- Shared sub-components ---

const Label = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <span style={labelStyle}>
    {icon} {text}
  </span>
);

// --- Field definitions ---

interface FieldDef {
  icon: React.ReactNode;
  label: string;
  color: string;
  value?: string | null;
  fallback: string;
  bold?: boolean;
  tag?: string;
}

const getCustomerFields = (customer: Customer): FieldDef[] => [
  {
    icon: <ShopOutlined />,
    label: 'Business name',
    color: '#722ed1',
    value: customer.businessName,
    fallback: 'No company provided',
  },
  {
    icon: <NumberOutlined />,
    label: 'Postcode',
    color: '#eb2f96',
    value: customer.postcode,
    fallback: 'No postcode provided',
  },
  {
    icon: <MailOutlined />,
    label: 'Email',
    color: BLUE.badge,
    value: customer.email,
    fallback: 'No email provided',
  },
  {
    icon: <EnvironmentOutlined />,
    label: 'City',
    color: DANGER.tag,
    value: customer.city,
    fallback: 'No city provided',
  },
  {
    icon: <MobileOutlined />,
    label: 'Phone',
    color: GREEN.badge,
    value: customer.phone,
    fallback: 'No phone provided',
  },
  {
    icon: <HomeOutlined />,
    label: 'Address',
    color: '#fa541c',
    value: customer.address,
    fallback: 'No address provided',
  },
  {
    icon: <PhoneOutlined />,
    label: 'Landline',
    color: YELLOW.badge,
    value: customer.landline,
    fallback: 'No landline provided',
  },
  {
    icon: <GlobalOutlined />,
    label: 'Country',
    color: '#13c2c2',
    value: customer.country,
    fallback: 'No country provided',
  },
];

const getDefaultExtraFields = (customer: Customer): FieldDef[] => [
  {
    icon: <HomeOutlined />,
    label: 'Address 2',
    color: '#fa541c',
    value: customer.secondAddress,
    fallback: 'No address provided',
  },
  {
    icon: <HomeOutlined />,
    label: 'Address 3',
    color: '#fa541c',
    value: customer.thirdAddress,
    fallback: 'No address provided',
  },
];

const getCustomerActivityFields = (customer: Customer): FieldDef[] => [
  {
    icon: <CalendarOutlined />,
    label: 'Customer since',
    color: ICON_GREY,
    value: customer.creationTime ? dayjs(customer.creationTime).format('DD/MM/YYYY') : undefined,
    fallback: '—',
  },
  {
    icon: <CalendarOutlined />,
    label: 'App registered',
    color: GREEN.badge,
    value: customer.appRegistrationDate
      ? dayjs(customer.appRegistrationDate).format('DD/MM/YYYY')
      : undefined,
    fallback: 'Not registered',
  },
  {
    icon: <CalendarOutlined />,
    label: 'Last job',
    color: YELLOW.badge,
    value: customer.lastJobBookingDate
      ? dayjs(customer.lastJobBookingDate).format('DD/MM/YYYY')
      : undefined,
    fallback: 'No jobs yet',
  },
  {
    icon: <TeamOutlined />,
    label: 'Total jobs',
    color: BLUE.badge,
    value: customer.totalJobCount != null ? String(customer.totalJobCount) : undefined,
    fallback: '0',
  },
];

const getVehicleFields = (vehicle: Vehicle): FieldDef[] => [
  {
    icon: <KeyOutlined />,
    label: 'VIN No.',
    color: '#13c2c2',
    value: vehicle.combinedVin,
    fallback: '—',
    tag: 'VIN',
  },
  {
    icon: <SafetyCertificateOutlined />,
    label: 'MOT date',
    color: DANGER.tag,
    value:
      vehicle.motExpiryDate &&
      dayjs(vehicle.motExpiryDate).isValid() &&
      !dayjs(vehicle.motExpiryDate).isSame(dayjs(0))
        ? dayjs(vehicle.motExpiryDate).format('DD/MM/YYYY')
        : undefined,
    fallback: 'Exempt from MOT',
    tag: 'MOT',
  },
];

// --- Main component ---

interface IProps {
  customer: Customer;
  vehicle?: Vehicle;
  /**
   * What an order already says about its vehicle. Lets the card show the plate and model without
   * loading the vehicle (an order may carry no VIN); the Details drawer fetches it when opened.
   */
  vehicleSummary?: { registrationNumber?: string; makeAndModel?: string; vin?: string };
  compact?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  bordered?: boolean;
  hideHeader?: boolean;
  fixedWidth?: boolean;
  onOpenChat?: () => void;
  /**
   * Alternative to onOpenChat for hosts with no chat drawer of their own (the invoice pages): the
   * card renders the Chat button and its own drawer on this Stream channel.
   */
  chatChannelId?: string;
  invoice?: Invoice;
  // Renders full-size, right-aligned "SMS Invite" + "Edit Customer" buttons instead of the
  // compact pen icon. Used on the dedicated customer page where these are primary actions.
  largeActions?: boolean;
  /** Mileage to base new reminders on; the order's when there is one, else the vehicle's own. */
  currentMileage?: number;
  /** Full layout: label/value pairs per row and label width - a narrow host (a drawer) asks for fewer. */
  descriptionColumns?: number;
  descriptionLabelWidth?: number;
}

export const CustomerDetailsCard: React.FC<IProps> = observer(
  ({ customer, vehicle, vehicleSummary, compact, collapsible = false, defaultExpanded = true, bordered = true, hideHeader = false, fixedWidth = false, onOpenChat, chatChannelId, invoice, largeActions = false, currentMileage, descriptionColumns, descriptionLabelWidth }) => {
    const { isMobile } = useView();
    const { customerStore, orderStore } = useStore();
    const { order: access } = useAccess();
    // A headerless card (the details drawer) shows no invite button, so it must not run the hook:
    // its cleanup would wipe the shared invite state the card underneath is still showing.
    const { customerInvite, loading: customerInviteLoading } = useCustomerSmsInvite(
      hideHeader ? undefined : customer?.id
    );
    const [readyForPickupLoading, setReadyForPickupLoading] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [editDrawerOpen, setEditDrawerOpen] = useState(false);
    const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
    const [vehicleDrawerOpen, setVehicleDrawerOpen] = useState(false);
    const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);
    const [ownChatOpen, setOwnChatOpen] = useState(false);
    // A host-owned drawer wins; otherwise the card opens its own when it was given a channel.
    const openChat = onOpenChat ?? (chatChannelId ? () => setOwnChatOpen(true) : undefined);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [remindersDrawerOpen, setRemindersDrawerOpen] = useState(false);
    const vehicleVin = vehicle?.combinedVin || vehicleSummary?.vin;
    const vehiclePlate = vehicle?.registrationNumber || vehicleSummary?.registrationNumber;
    const vehicleMakeAndModel =
      `${vehicle?.combinedMake || ''} ${vehicle?.combinedModel || ''}`.trim() ||
      vehicleSummary?.makeAndModel ||
      '';
    const hasVehicle = !!vehicle || !!vehiclePlate;
    // A job raised without a VIN gets the one a refresh in the details drawer finds, and the make and
    // model as reported (the plate it was raised with is left alone). Only for the same plate.
    const handleVehicleRefreshed = useCallback(
      (refreshed: Vehicle | null) => {
        if (!vehicleSummary || vehicleSummary.vin || !refreshed?.combinedVin) return;
        const normalize = (reg?: string) => reg?.replace(/s/g, '').toUpperCase();
        if (normalize(refreshed.registrationNumber) !== normalize(vehicleSummary.registrationNumber)) return;
        orderStore.setOrderVehicle({
          vin: refreshed.combinedVin,
          vehicleModel:
            `${refreshed.combinedMake ?? ''} ${refreshed.combinedModel ?? ''}`.trim() ||
            vehicleSummary.makeAndModel ||
            '',
        });
      },
      [vehicleSummary, orderStore]
    );
    const { loading: overdueLoading, overdueCount } = useOverdueReminderCount(vehicleVin);
    const reminderMileage = currentMileage ?? vehicle?.mileage;

    // Everything else we know about the vehicle lives behind these two, so the card stays a
    // summary and the order screen keeps its tab bar for the job itself.
    // Three equal plain buttons filling the card width: they open detail, not drive the job, so
    // they stay quieter than the primary actions elsewhere on the screen.
    const vehicleActions = (style: React.CSSProperties) => (
      <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : undefined, gap: 8, ...style }}>
        <Button
          block
          size="small"
          icon={<CarOutlined />}
          style={{ flex: 1 }}
          onClick={() => setVehicleDrawerOpen(true)}
        >
          Details
        </Button>
        <Button
          block
          size="small"
          icon={<HistoryOutlined />}
          style={{ flex: 1 }}
          onClick={() => setHistoryDrawerOpen(true)}
        >
          History
        </Button>
        {vehicleVin && (
          <Badge
            count={overdueLoading ? 0 : overdueCount}
            size="small"
            offset={[-2, 2]}
            // Red, like the toolbar's pending-estimate badge: these are overdue, something to act on,
            // not an informational count.
            style={{ zIndex: 1 }}
            styles={{ root: { flex: 1, display: 'flex' } }}
          >
            <Button
              block
              size="small"
              icon={<BellOutlined />}
              onClick={() => setRemindersDrawerOpen(true)}
            >
              Reminders
            </Button>
          </Badge>
        )}
      </div>
    );
    const [expanded, setExpanded] = useState(defaultExpanded);

    const handleSendSms = useCallback(async () => {
      if (!customer) return;
      setSmsSending(true);
      await NotificationService.sendCustomerSmsInvitation(customer.id).then(() => {
        customerStore.setCustomerSmsInvite({
          id: customer.id,
          type: 'SmsInvite',
          customerId: customer.id,
          creationDate: dayjs().format(),
        });
      });
      setSmsSending(false);
    }, [customer, customerStore]);

    const onReadyForPickupConfirm = useCallback(async () => {
      if (!invoice?.registrationNumber || !invoice?.id) return;
      setReadyForPickupLoading(true);
      try {
        await NotificationService.readyForPickup({
          registrationNumber: invoice.registrationNumber,
          sendPush: true,
          invoiceId: invoice.id,
        });
      } finally {
        setReadyForPickupLoading(false);
      }
    }, [invoice]);

    const nameTag = (
      <AppUserTag userId={customer.userId} style={{ margin: 0 }} />
    );

    // Opens the customer's details in a new tab, same as clicking a row in the customers table.
    const renderCustomerName = (fontSize: number) => (
      <strong
        onClick={(e) => {
          e.stopPropagation();
          window.open(`/my-customers-view/${customer.id}`, '_blank');
        }}
        style={{
          fontSize,
          color: PRIMARY,
          cursor: 'pointer',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title="Open customer details in a new tab"
      >
        {customer.fullname}
      </strong>
    );

    // The walk-in record is shared by every QuickPay sale: never edited, only swapped out per invoice.
    const isWalkIn = !!customer.isWalkIn;
    const canAssign = isWalkIn && !!invoice;
    const canEdit = (!isWalkIn || canAssign) && access.customerEdit;
    const handleEditClick = () => (canAssign ? setAssignDrawerOpen(true) : setEditDrawerOpen(true));

    // Labelled rather than icon-only: not every user reads a pen as "edit". Small pills keep the
    // header row to one line next to the name; the name truncates before the buttons give way.
    const editButton = canEdit && (
      <Button
        type="primary"
        shape="round"
        size="small"
        icon={<EditOutlined />}
        onClick={handleEditClick}
      >
        {canAssign ? 'Assign' : 'Edit'}
      </Button>
    );

    const chatButton = customer.userId !== null && openChat && (
      <Button type="primary" shape="round" size="small" icon={<CommentOutlined />} onClick={openChat}>
        Chat
      </Button>
    );

    const customerFields = getCustomerFields(customer);
    const vehicleFields = vehicle ? getVehicleFields(vehicle) : [];

    // A one-line confirmation attached to the button it belongs to, the way the rest of the app
    // confirms a single action - a modal overlay for one text message was more ceremony than the
    // thing deserved. The number is spelled out: it is going to a real phone.
    const inviteConfirm = (trigger: React.ReactNode) => (
      <Popconfirm
        icon={<MessageOutlined style={{ color: GREEN.badge }} />}
        title="Invite to the Umoto app"
        description={
          <span style={{ display: 'block', maxWidth: 260 }}>
            {customer.fullname} gets a text on <strong>{customer.phone}</strong> with a link to
            download the app, where they can follow this job and talk to the garage.
          </span>
        }
        okText="Send invite"
        cancelText="Cancel"
        onConfirm={handleSendSms}
        okButtonProps={{ loading: smsSending }}
      >
        {trigger}
      </Popconfirm>
    );

    // Same look as the confirmation, but informational only. A disabled button swallows hover
    // events, so the span is the hover target and the button ignores the pointer.
    const inviteComingSoon = (trigger: React.ReactNode) => (
      <Popover
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageOutlined style={{ color: GREEN.badge }} />
            Invite to the Umoto app
          </span>
        }
        content={
          <span style={{ display: 'block', maxWidth: 260 }}>
          Soon you’ll be able to send customers a personalised SMS invite to download the Umoto app, branded for your garage. Coming soon.
          </span>
        }
      >
        <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>{trigger}</span>
      </Popover>
    );

    const inviteWrapper = SMS_INVITE_ENABLED ? inviteConfirm : inviteComingSoon;
    const inviteDisabled = !SMS_INVITE_ENABLED || !!customerInvite;
    const inviteButtonStyle: React.CSSProperties | undefined = SMS_INVITE_ENABLED
      ? undefined
      : { pointerEvents: 'none' };

    const smsInviteButton = customer &&
      customer.userId === null &&
      customer.phone &&
      inviteWrapper(
        <SuccessButton
          size="small"
          disabled={inviteDisabled}
          loading={customerInviteLoading}
          style={inviteButtonStyle}
        >
          {!!customerInvite ? 'Invited' : 'SMS Invite'}
        </SuccessButton>
      );

    const readyForPickupButton = !!invoice?.registrationNumber && (
      <Popconfirm
        title="Inform the customer that their vehicle is ready for pick up?"
        okText="Yes, inform"
        cancelText="Don't inform"
        onConfirm={onReadyForPickupConfirm}
        okButtonProps={{ loading: readyForPickupLoading }}
      >
        <Button type="primary" size="small" loading={readyForPickupLoading}>
          Ready for pickup
        </Button>
      </Popconfirm>
    );

    // Top-right action buttons for the dedicated customer page. Full-text on desktop;
    // icon-only on mobile to save space.
    const showInvite = customer.userId === null && !!customer.phone;
    const actionButtons = (
      <>
        {showInvite &&
          inviteWrapper(
            isMobile ? (
              <SuccessButton
                shape="circle"
                aria-label={customerInvite ? 'Invited' : 'SMS Invite'}
                icon={<MessageOutlined />}
                disabled={inviteDisabled}
                loading={customerInviteLoading}
                style={inviteButtonStyle}
              />
            ) : (
              <SuccessButton
                icon={<MessageOutlined />}
                disabled={inviteDisabled}
                loading={customerInviteLoading}
                style={inviteButtonStyle}
              >
                {customerInvite ? 'Invited' : 'SMS Invite'}
              </SuccessButton>
            )
          )}
        {canEdit &&
          (isMobile ? (
            <Button
              type="primary"
              shape="circle"
              aria-label={canAssign ? 'Assign customer' : 'Edit customer'}
              icon={<EditOutlined />}
              onClick={handleEditClick}
            />
          ) : (
            <Button type="primary" icon={<EditOutlined />} onClick={handleEditClick}>
              {canAssign ? 'Assign Customer' : 'Edit Customer'}
            </Button>
          ))}
      </>
    );

    const modals = (
      <>
        {!isWalkIn && (
          <CustomerEditDrawer
            open={editDrawerOpen}
            customer={customer}
            onClose={() => setEditDrawerOpen(false)}
          />
        )}
        {canAssign && invoice && (
          <InvoiceAssignCustomerDrawer
            open={assignDrawerOpen}
            invoiceId={invoice.id}
            onClose={() => setAssignDrawerOpen(false)}
          />
        )}
        {!onOpenChat && chatChannelId && customer.userId && (
          <ChatDrawer
            open={ownChatOpen}
            channelId={chatChannelId}
            customerUserId={customer.userId}
            title={`Chat with ${customer.fullname}`}
            onClose={() => setOwnChatOpen(false)}
          />
        )}
        <CustomerDetailsDrawer
          open={customerDrawerOpen}
          customer={customer}
          onClose={() => setCustomerDrawerOpen(false)}
        />
        <VehicleDetailsDrawer
          open={vehicleDrawerOpen}
          vehicle={vehicle}
          vin={vehicleVin}
          registrationNumber={vehiclePlate}
          makeAndModel={vehicleMakeAndModel}
          setSelectedVehicle={handleVehicleRefreshed}
          onClose={() => setVehicleDrawerOpen(false)}
        />
        <ServiceHistoryDrawer
          open={historyDrawerOpen}
          vin={vehicleVin}
          registrationNumber={vehiclePlate}
          onClose={() => setHistoryDrawerOpen(false)}
        />
        {vehicleVin && (
          <RemindersDrawer
            open={remindersDrawerOpen}
            vin={vehicleVin}
            registrationNumber={vehiclePlate ?? ''}
            customerId={customer.id}
            currentMileage={reminderMileage}
            onClose={() => setRemindersDrawerOpen(false)}
          />
        )}
      </>
    );

    if (compact) {
      // Two clearly separate panels - what is being worked on, and whose it is (the vehicle first, as in the New Booking drawer). DetailsPanel draws the box, so the
      // booking drawer's own customer/vehicle boxes are the same thing rather than a near-miss of it.
      const compactPanels = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hasVehicle && (
            <DetailsPanel actions={vehicleActions({ flex: 1 })}>
              {vehiclePlate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <RegPlate>{vehiclePlate}</RegPlate>
                  {vehicleMakeAndModel && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(0, 0, 0, 0.65)' }}>
                      {vehicleMakeAndModel}
                    </span>
                  )}
                </div>
              )}
              {/* Only order hosts pass a summary; there a missing VIN is worth flagging, since
                  reminders and the vehicle record depend on it. */}
              {vehicleSummary && !vehicleVin && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: vehiclePlate ? 8 : 0, padding: '4px 8px', fontSize: 12 }}
                  message="VIN missing - check the vehicle details and confirm the vehicle is correct."
                />
              )}
            </DetailsPanel>
          )}

          <DetailsPanel
            extra={
              (readyForPickupButton || smsInviteButton) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {readyForPickupButton}
                  {smsInviteButton}
                </div>
              )
            }
            actions={
              <>
                {access.customerDetails && (
                  <Button
                    block
                    size="small"
                    icon={<UserOutlined />}
                    style={{ flex: 1 }}
                    onClick={() => setCustomerDrawerOpen(true)}
                  >
                    Details
                  </Button>
                )}
                {canEdit && (
                  <Button
                    block
                    size="small"
                    icon={<EditOutlined />}
                    style={{ flex: 1 }}
                    onClick={handleEditClick}
                  >
                    {canAssign ? 'Assign' : 'Edit'}
                  </Button>
                )}
                {/* Always in the row so it keeps its shape, whoever the customer is; only one
                    on the app has a channel to open. */}
                <Tooltip
                  title={
                    customer.userId !== null && openChat
                      ? undefined
                      : 'Only customers on the app can be messaged'
                  }
                >
                  <Button
                    block
                    size="small"
                    icon={<CommentOutlined />}
                    style={{ flex: 1 }}
                    disabled={customer.userId === null || !openChat}
                    onClick={openChat}
                  >
                    Chat
                  </Button>
                </Tooltip>
              </>
            }
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {renderCustomerName(14)}
              {nameTag}
            </div>
          </DetailsPanel>
        </div>
      );

      // The customer's page with this vehicle selected, in a new tab: everything the two panels only
      // summarise. On the card it sits at the foot (the card is as tall as the one beside it, and the
      // two panels leave a slab of empty space under them).
      const fullDetailsUrl = `/my-customers-view/${customer.id}?activeTab=Details&regNumber=${encodeURIComponent(vehiclePlate ?? '')}`;
      const fullDetailsButton = (
        // A quiet note with its link in the sentence, not another button: it says what is on the page (the
        // card is the summary) and where to press, without a border to draw the eye from the panels.
        <div
          style={{
            padding: '0 4px',
            fontSize: 14,
            lineHeight: '22px',
            color: 'rgba(0, 0, 0, 0.45)',
            ...(isMobile && { marginTop: 12 }),
          }}
        >
          Everything about this customer and vehicle in one place: past bookings, inspections,
          estimates and more.{' '}
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            style={{ padding: 0, height: 'auto', fontSize: 14, fontWeight: 600 }}
            onClick={() => window.open(fullDetailsUrl, '_blank', 'noopener,noreferrer')}
          >
            View full details
          </Button>
        </div>
      );

      return (
        <>
          {/* On the phone the panels are the card: the collapse they sit in already frames them, so a
              white card around two more boxes is just a margin. */}
          {isMobile ? (
            <>
              {compactPanels}
              {fullDetailsButton}
            </>
          ) : (
            <SharedCard fillHeight>
              {compactPanels}
              {fullDetailsButton}
            </SharedCard>
          )}
          {modals}
        </>
      );
    }

    const customerFieldsFull = [
      ...customerFields.slice(0, 6),
      ...getDefaultExtraFields(customer),
      ...customerFields.slice(6),
    ];

    const descProps = {
      size: 'small' as const,
      column: descriptionColumns ?? ({ xs: 1, sm: 1, md: 3 } as const),
      bordered: true,
      styles: {
        label: {
          fontWeight: 'bold' as const,
          whiteSpace: 'nowrap' as const,
          ...((descriptionLabelWidth ?? (fixedWidth ? 140 : undefined)) && {
            width: descriptionLabelWidth ?? 140,
          }),
        },
        content: fixedWidth && !descriptionLabelWidth ? { width: 200 } : undefined,
      },
    };

    const renderDescItems = (fields: FieldDef[]) => (
      <>
        {fields.map((f, i) => (
          <Descriptions.Item
            key={i}
            label={
              <Label
                icon={React.cloneElement(f.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
                  style: { color: ICON_GREY },
                })}
                text={f.label}
              />
            }
          >
            {f.value ? (
              f.bold ? (
                <strong>{f.value}</strong>
              ) : (
                f.value
              )
            ) : (
              // A dash, with the wordy reason on hover: spelled out, the fallbacks were the widest
              // thing in the grid.
              <span style={emptyStyle} title={f.fallback}>
                —
              </span>
            )}
          </Descriptions.Item>
        ))}
        {fields.length % 3 !== 0 &&
          Array.from({ length: 3 - (fields.length % 3) }).map((_, i) => (
            <Descriptions.Item key={`spacer-${i}`} label={<span />}>
              {' '}
            </Descriptions.Item>
          ))}
      </>
    );

    const cardContent = (
      <>
        {!hideHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              marginBottom: collapsible && !expanded ? 8 : 12,
              cursor: collapsible ? 'pointer' : undefined,
            }}
            onClick={collapsible ? () => setExpanded((prev) => !prev) : undefined}
          >
            {renderCustomerName(16)}
            {nameTag}
            {largeActions ? (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                {actionButtons}
              </span>
            ) : (
              <>
                {smsInviteButton}
                {readyForPickupButton}
                <span onClick={(e) => e.stopPropagation()}>{editButton}</span>
                {chatButton && <span onClick={(e) => e.stopPropagation()}>{chatButton}</span>}
              </>
            )}
            {collapsible && (
              <span style={{ marginLeft: 'auto' }}>
                {expanded ? <UpOutlined /> : <DownOutlined />}
              </span>
            )}
          </div>
        )}
        {(!collapsible || expanded) && (
          <>
            <Descriptions {...descProps} style={{ marginTop: 8 }}>
              {renderDescItems(customerFieldsFull)}
            </Descriptions>
            {vehicle?.registrationNumber && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <RegPlate>{vehicle.registrationNumber}</RegPlate>
                  {(vehicle.combinedMake || vehicle.combinedModel) && (
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0, 0, 0, 0.65)' }}>
                      {`${vehicle.combinedMake || ''} ${vehicle.combinedModel || ''}`.trim()}
                    </span>
                  )}
                </div>
              </>
            )}
            <Descriptions {...descProps}>{renderDescItems(vehicleFields)}</Descriptions>
            {hasVehicle && vehicleActions({ marginTop: 12 })}
            {fixedWidth && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Descriptions {...descProps}>
                  {renderDescItems(getCustomerActivityFields(customer))}
                </Descriptions>
              </>
            )}
          </>
        )}
      </>
    );

    return (
      <>
        {bordered ? <SharedCard size="small">{cardContent}</SharedCard> : cardContent}
        {modals}
      </>
    );
  }
);
